import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Mail, Plus } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const easeOut = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.7, ease: easeOut, delay }}
  >
    {children}
  </motion.div>
);

// Supports plain paragraphs and bullet lines ("-", "*", "•").
const RichText = ({ text }) => {
  const lines = (text || "").split("\n");
  const blocks = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="space-y-2.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-[#A1A1AA]">
              <span className="mt-[2px] text-[#F4F4F5]">—</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  lines.forEach((raw) => {
    const line = raw.trim();
    if (/^[-*•]\s*/.test(line)) bullets.push(line.replace(/^[-*•]\s*/, ""));
    else {
      flush();
      if (line) blocks.push(<p key={`p-${blocks.length}`} className="text-[#A1A1AA]">{line}</p>);
    }
  });
  flush();
  return <div className="space-y-4 text-base leading-relaxed md:text-lg">{blocks}</div>;
};

const EmptyFrame = ({ index }) => (
  <div className="absolute inset-0 blueprint">
    <span className="absolute left-3 top-3 h-4 w-4 border-l border-t border-white/25" />
    <span className="absolute right-3 top-3 h-4 w-4 border-r border-t border-white/25" />
    <span className="absolute bottom-3 left-3 h-4 w-4 border-b border-l border-white/25" />
    <span className="absolute bottom-3 right-3 h-4 w-4 border-b border-r border-white/25" />
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
      <span className="font-display text-2xl font-black tracking-tight text-white/15">JH</span>
      <Plus className="h-4 w-4 text-white/25" strokeWidth={1.25} />
      <span className="mono text-[9px] tracking-[0.3em] text-white/25">PHOTO {index}</span>
    </div>
  </div>
);

const Tile = ({ path, index, aspect }) => (
  <div
    data-testid={`about-image-${index}`}
    className={`group relative ${aspect} w-full overflow-hidden rounded-2xl border border-white/10 bg-[#121215] shadow-[0_20px_60px_-25px_rgba(0,0,0,0.9)]`}
  >
    {path ? (
      <img
        src={fileUrl(path)}
        alt=""
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
      />
    ) : (
      <EmptyFrame index={index} />
    )}
  </div>
);

export default function PublicSite() {
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get("/content").then(({ data }) => setC(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!c) return;
    document.title = c.brand_name || "Jack Heffernan SC";
    if (c.favicon_path) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = fileUrl(c.favicon_path);
    }
    const setMeta = (attr, key, content) => {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("property", "og:title", c.brand_name || "Jack Heffernan SC");
    if (c.meta_description) {
      setMeta("name", "description", c.meta_description);
      setMeta("property", "og:description", c.meta_description);
    }
    if (c.share_image_path) {
      setMeta("property", "og:image", fileUrl(c.share_image_path));
      setMeta("name", "twitter:card", "summary_large_image");
    }
  }, [c]);

  if (!c) return <div className="min-h-screen bg-[#09090B]" />;

  return (
    <main className="relative min-h-screen bg-[#09090B] text-[#F4F4F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#09090B]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center px-6 py-5 sm:px-8">
          <span data-testid="brand-header" className="font-display text-base font-black uppercase tracking-tight md:text-lg">
            {c.brand_name}
          </span>
        </div>
      </header>

      {/* Hero — plain black, no photo */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 pt-10 pb-10 sm:px-8 md:pb-12 md:pt-12">
          <div className="max-w-4xl">
            <motion.h1
              data-testid="hero-title"
              className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: easeOut }}
            >
              {c.hero_title}
            </motion.h1>
            <motion.div
              className="mt-6"
              data-testid="hero-description"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.7, ease: easeOut }}
            >
              <RichText text={c.hero_description} />
            </motion.div>
            <motion.div
              className="mt-9"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7, ease: easeOut }}
            >
              <a
                data-testid="apply-cta-btn"
                href={c.apply_url || "#"}
                target={c.apply_url ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 rounded-xl bg-[#FAFAFA] px-9 py-4 text-sm font-bold uppercase tracking-wide text-[#09090B] shadow-[0_0_30px_rgba(255,255,255,0.12)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_46px_rgba(255,255,255,0.22)] active:scale-[0.98]"
              >
                {c.apply_label}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About — text then two photos */}
      <section id="about" className="border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 pt-10 pb-16 sm:px-8 md:pt-14 md:pb-24">
          <Reveal className="max-w-4xl">
            <h2 data-testid="about-heading" className="font-display text-4xl font-black uppercase tracking-tight md:text-5xl">
              {c.about_heading}
            </h2>
            <p data-testid="about-text" className="mt-6 whitespace-pre-line text-base leading-relaxed text-[#A1A1AA] md:text-lg">
              {c.about_text}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Tile path={c.image1_path} index="1" aspect="aspect-[4/3]" />
            <Tile path={c.image2_path} index="2" aspect="aspect-[4/3]" />
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="mx-auto max-w-6xl px-6 py-12 sm:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-display text-lg font-black uppercase tracking-tight">{c.brand_name}</span>
          <div className="flex flex-wrap items-center gap-6">
            {(c.links || []).filter((l) => l.label && l.url).map((l, i) => (
              <a
                key={l.id || i}
                data-testid={`footer-link-${i + 1}`}
                href={l.url}
                target={l.url.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="text-sm text-[#A1A1AA] transition-colors hover:text-[#F4F4F5]"
              >
                {l.label}
              </a>
            ))}
            {c.contact_email && (
              <a
                data-testid="footer-email"
                href={`mailto:${c.contact_email}`}
                className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] transition-colors hover:text-[#F4F4F5]"
              >
                <Mail className="h-4 w-4" /> {c.contact_email}
              </a>
            )}
            {c.instagram_url && (
              <a
                data-testid="footer-instagram"
                href={c.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] transition-colors hover:text-[#F4F4F5]"
              >
                <Instagram className="h-4 w-4" /> Instagram
              </a>
            )}
          </div>
        </div>
      </footer>
    </main>
  );
}
